import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./Studio.css";
import StudioForm from "./StudioForm";
import { getStudios, deleteStudio, getTeamLeads } from "../../services/api/api";
import "primeicons/primeicons.css";
import Swal from "sweetalert2";
import { ToastContainer } from "react-toastify";
import { showToast, fetchData } from "../utils/Function/Helper";

export default function Studio() {
  const user = useSelector((state) => state.user);
  const [formShow, setFormShow] = useState(false);
  const [selectedStudioData, setSelectedStudioData] = useState(null); // Tracks selected studio ID
  const [studios, setStudios] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(null); // Manage dropdown visibility
  const [deleteRender, setDeleteRender] = useState(false);
  const [popupData, setPopupData] = useState();
  const [teamleads, setTeamleads] = useState([]);

  // Fetch studios
  useEffect(() => {
    const fetchStudios = async () => {
      try {
        const data = await getStudios({
          superadmin_id: user?.userDetails?.ID || null,
          teamlead_id: user?.userDetails?.TeamLeadID || null,
        });
        setStudios(data);
        fetchData(
          () => getTeamLeads({ superadmin_id: user?.userDetails?.ID || null }),
          setTeamleads
        );
      } catch (error) {
        showToast("error", error.message);
      }
    };
    if (!formShow) {
      fetchStudios();
    }
  }, [user, formShow, deleteRender]);

  // Finish dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (studio) => {
      // Finish dropdown on outside click
      if (!studio.target.closest(".dropdown-menu-layer")) {
        setDropdownVisible(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle dropdown visibility for a specific card
  const toggleDropdown = (id) => {
    setDropdownVisible(dropdownVisible === id ? null : id);
  };

  // Handle form show logic
  const handleFormShow = (studioData) => {
    if (formShow) {
      Swal.fire({
        title: "Are you sure you want to cancel?",
        text: "If you leave, your changes will be lost!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#28a745",
        confirmButtonText: "Yes, cancel",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          setSelectedStudioData(studioData);
          setFormShow(true);
        }
      });
    } else {
      setSelectedStudioData(studioData);
      setFormShow(true);
    }
  };

  // Handle delete logic
  const handleDelete = (data) => {
    Swal.fire({
      title: "Are you sure you want to delete!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#28a745",
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
      customClass: {
        title: "swal2-title-custom",
        htmlContainer: "swal2-text-custom",
        confirmButton: "swal2-confirm-button-custom",
        cancelButton: "swal2-cancel-button-custom",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteStudio(data); // Call the API
          if (response.status) {
            Swal.fire({
              title: `${response.message}`,
              icon: "success",
              confirmButtonColor: "#dc3545",
              confirmButtonText: "Okay",
              customClass: {
                title: "swal2-title-custom",
                htmlContainer: "swal2-text-custom",
                confirmButton: "swal2-confirm-button-custom",
              },
            }).then(() => {
              setDeleteRender(!deleteRender);
            });
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        }
      }
    });
  };

  const handleCardClick = (data) => {
    setPopupData(data);
  };
  // generate teamlead option for dropdown

  const generateTL = (id) => {
    if (teamleads.length) {
      const name = teamleads.filter((item) => {
        if (item.id == id) {
          return item;
        }
      });
      return name[0].teamlead_name;
    } else {
      return "N/A";
    }
  };

  return (
    <div className="event-container">
      <ToastContainer />

      <div className="event-head">
        <div>
          <h2 className="hcolor">Studios List</h2>
        </div>

        {user?.userDetails?.UserRole === "Super Admin" && (
          <div>
            <button
              type="button"
              disabled={formShow}
              className="btn btn-outline-primary create-btn-custom"
              onClick={() => handleFormShow(null)}
            >
              Create New Studio
            </button>
          </div>
        )}
      </div>

      <div className="event-body">
        {formShow && (
          <StudioForm
            teamleads={teamleads}
            setFormShow={setFormShow}
            studioData={selectedStudioData}
            setSelectedStudioData={setSelectedStudioData}
          />
        )}

        {!formShow && (
          <div className="event-section">
            <div className="events-container">
              {studios.data ? (
                studios.data.map((card) => (
                  <div
                    className="card event-card"
                    key={card.id}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0 hcolor">
                        {card.studio_name}
                      </h5>
                      <div className="position-relative">
                        <button
                          className="btn card-setting-btn"
                          onClick={() => {
                            toggleDropdown(card.id);
                          }}
                        >
                          <svg width={20} className="gear-svg" viewBox="0 0 512 512">
                            <path d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z" />
                          </svg>
                        </button>

                        {/* Dropdown Menu */}
                        {dropdownVisible === card.id && (
                          <div
                            className="dropdown-menu-layer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ul className="list-unstyled mb-0">
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => {
                                    handleFormShow(card);
                                  }}
                                >
                                  Edit
                                </button>
                              </li>
                              {user?.userDetails?.UserRole ===
                                "Super Admin" && (
                                <li>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() => {
                                      handleDelete(card);
                                    }}
                                  >
                                    Delete
                                  </button>
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="card-body">
                      <p className="card-text hcolor">
                        <strong>Duration : </strong>
                        {card.duration} Hrs
                      </p>
                      <p className="card-text hcolor">
                        <strong>Buffer : </strong>
                        {card.buffer} Hrs
                      </p>
                      <p className="card-text hcolor">
                        <strong>Team Lead : </strong>
                        {generateTL(card.teamlead_id)}
                      </p>

                      <div className="card-footer-custom">
                        <button
                          className="btn btn-view btn-outline-secondary"
                          data-bs-toggle="modal"
                          data-bs-target="#exampleModal"
                          onClick={() => {
                            handleCardClick(card);
                          }}
                        >
                          {" "}
                          View
                          <i className="pi pi-eye"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="noevent">There no studio created yet!</div>
              )}
            </div>
          </div>
        )}

        <div
          className="modal fade"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          id="exampleModal"
          tabIndex="-1"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered custom-view-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">
                  {popupData ? popupData.studio_name : "Modal Title"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body custom-card-body">
                <p>
                  <strong>Studio Name : </strong>
                  {popupData && popupData.studio_name}
                </p>
                <p>
                  <strong>Duration : </strong>
                  {popupData && popupData.duration} Hrs
                </p>
                <p>
                  <strong>Buffer Time : </strong>
                  {popupData && popupData.buffer} Hrs
                </p>
                <p>
                  <strong>Assigned to Team Lead : </strong>
                  {popupData && generateTL(popupData.teamlead_id)}
                </p>
                <p>
                  <strong>Maximum Schedule Days : </strong>
                  {popupData && popupData.day_limit} Days
                </p>
                <p>
                  <strong>Availability : </strong>
                </p>
                {popupData?.availability?.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>Time Slots</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popupData.availability.map((day) => (
                        <tr key={day.day}>
                          <td>{day.day}</td>
                          <td>
                            {day.times.length > 0 ? (
                              day.times.map((time, index) => (
                                <div key={index}>
                                  {time.start} - {time.end}
                                </div>
                              ))
                            ) : (
                              <span>No Time Slots</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No availability provided.</p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
